import React from 'react';
import LongText from '../Elements/LongText';
import FieldTypesEnum from '../Enums/FieldTypesEnum';
import TabListTableCellWidthEnum from '../Enums/TabListTableCellWidthEnum';
import JsxHelper from '../Helpers/JsxHelper';
import stringHelper from '../Helpers/stringHelper';

class OpportunitySecurityChecklistItem {
  constructor(item) {
    // No item = a checklist that does not exist yet, so this branch is also what a new one starts with: the
    // table's NOT NULL notification periods and the wording the business expects for the retention fields.
    if (item == null) {
      this.OpportunitySecurityChecklistId = '';
      this.ApprovedLogoUsage = '';
      this.ApprovedNameUsage = '';
      this.AuthorizedClientBusinessRep = '';
      this.ClientAccountContactsData = 'according to the civil limitation period for a particular contract';
      this.ClientOperationalContactsData = 'according to the civil limitation period for a particular contract';
      this.ClientOperationalContactsExport = '';
      this.ContactEmail = '';
      this.ContactName = '';
      this.ContactPhone = '';
      this.DataFlowMap = [];
      this.DeliveredEvaluationDetails = 'indefinite in an anonymized format';
      this.DeliveredEvaluationDetailsExport = '';
      this.ErasureNotification = 2;
      this.PublicInformationClassification = '';
      this.ConfidentialInformationClassification = '';
      this.InternalInformationClassification = '';
      this.RestrictedInformationClassification = '';
      this.OpportunityId = '';
      this.PrivacyBreachNotification = 24;
      this.SecurityIncidentNotification = 24;
      this.SubjectInquiryNotification = 2;
      this.DataFlowMapFreeText = '';
      this.ClientId = '';
      this.DataFlowMapDisplay = '';
      this.AuthorizedClientBusinessRepNames = '';
      this.Comment = '';
      // Section A (Data Transfer) / Section B (Data Retention).
      this.DataTransferRequired = false;
      this.DataRetentionDefined = false;
      this.DataRetainUntilUtc = '';
      this.DataRetainUntilDisplay = '';
      this.ActualEndOn = null;
      this.RetentionDetail = '';
      this.HasRetentionDetails = false;
      return;
    }
    this.OpportunitySecurityChecklistId = item.opportunitySecurityChecklistId;
    this.ApprovedLogoUsage = item.approvedLogoUsage;
    this.ApprovedNameUsage = item.approvedNameUsage;
    this.AuthorizedClientBusinessRep = item.authorizedClientBusinessRep;
    this.ClientAccountContactsData = item.clientAccountContactsData;
    this.ClientAccountContactsDataDisplay = stringHelper.formatWithUnit(item.clientAccountContactsData, 'months');
    this.ClientOperationalContactsData = item.clientOperationalContactsData;
    this.ClientOperationalContactsDataDisplay = stringHelper.formatWithUnit(
      item.clientOperationalContactsData,
      'months'
    );
    this.ClientOperationalContactsExport = item.clientOperationalContactsExport;
    this.ContactEmail = item.contactEmail;
    this.ContactName = item.contactName;
    this.ContactPhone = item.contactPhone;
    this.DataFlowMap = item.dataFlowMap;
    this.DeliveredEvaluationDetails = item.deliveredEvaluationDetails;
    this.DeliveredEvaluationDetailsDisplay = stringHelper.formatWithUnit(item.deliveredEvaluationDetails, 'months');
    this.DeliveredEvaluationDetailsExport = item.deliveredEvaluationDetailsExport;
    this.ErasureNotification = item.erasureNotification;
    // Shown with their unit inline in the read view (never a bare number).
    this.SecurityIncidentNotificationDisplay = stringHelper.formatWithUnit(item.securityIncidentNotification, 'hours');
    this.PrivacyBreachNotificationDisplay = stringHelper.formatWithUnit(item.privacyBreachNotification, 'hours');
    this.SubjectInquiryNotificationDisplay = stringHelper.formatWithUnit(
      item.subjectInquiryNotification,
      'working days'
    );
    this.ErasureNotificationDisplay = stringHelper.formatWithUnit(item.erasureNotification, 'working days');
    this.PublicInformationClassification = item.publicInformationClassification;
    this.ConfidentialInformationClassification = item.confidentialInformationClassification;
    this.InternalInformationClassification = item.internalInformationClassification;
    this.RestrictedInformationClassification = item.restrictedInformationClassification;
    this.OpportunityId = item.opportunityId;
    this.PrivacyBreachNotification = item.privacyBreachNotification;
    this.SecurityIncidentNotification = item.securityIncidentNotification;
    this.SubjectInquiryNotification = item.subjectInquiryNotification;
    this.DataFlowMapFreeText = item.dataFlowMapFreeText;
    this.ClientId = item.clientId;
    this.DataFlowMapDisplay = Array.isArray(item.dataFlowMap) ? item.dataFlowMap.join(', ') : '';
    this.AuthorizedClientBusinessRepNames = item.authorizedClientBusinessRepNames;
    this.Comment = item.comment;
    // Section A (Data Transfer) / Section B (Data Retention).
    this.DataTransferRequired = item.dataTransferRequired;
    this.DataRetentionDefined = item.dataRetentionDefined;
    this.DataRetainUntilUtc = item.dataRetainUntilUtc;
    this.DataRetainUntilDisplay =
      item.dataRetainUntilUtc === null || item.dataRetainUntilUtc === undefined || item.dataRetainUntilUtc === ''
        ? ''
        : String(item.dataRetainUntilUtc).substring(0, 10);
    // Read-only context from the joined Opportunity (Section B Retain Until validation).
    this.ActualEndOn = item.actualEndOn;
    // A new entry for the append-only retention log, saved with the checklist; never read back (the log is its own
    // list). Only an extraction carries one. HasRetentionDetails tells the wizard whether that entry is required;
    // left as it comes (no !!), so an extraction, which has none, does not overwrite the stored flag on import.
    this.RetentionDetail = item.retentionDetail ?? '';
    this.HasRetentionDetails = item.hasRetentionDetails;
  }

  static NameOfTheIdField() {
    return 'OpportunityId';
  }

  static nameOfTheItemDetailList = (item) => (
    <LongText
      text={`Opportunity #${item.OpportunityId}`}
      fitToBox
    />
  );

  static MoneyFields = [];

  static Currency() {
    return '';
  }

  static MoneyFieldsSecondary = [];

  static CurrencySecondary() {
    return '';
  }

  static NavigationDesc = [];
  static DateFields = [];

  static BoolFields = [
    'ClientOperationalContactsExport',
    'DeliveredEvaluationDetailsExport',
    'DataTransferRequired',
    'DataRetentionDefined',
  ];

  static FieldsToShowInListTable = [
    {
      tabName: 'General',
      tabLabel: 'General',
      icon: 'fa-solid fa-users',
      data: [
        { name: '-', label: '' },
        { name: '-message-', label: 'Contact' },
        { name: 'ContactName', label: 'InfoSec and Privacy Contact Name', className: 'text-left' },
        { name: 'ContactEmail', label: 'InfoSec and Privacy Contact Email', className: 'text-left' },
        { name: 'ContactPhone', label: 'InfoSec and Privacy Contact Phone', className: 'text-left' },
        {
          name: 'AuthorizedClientBusinessRepNames',
          label: 'Authorized Client Business Representative',
          className: 'text-left',
        },
        {
          name: 'ApprovedLogoUsage',
          label: ' Client Logo usage approved?',
          className: 'text-left',
        },
        {
          name: 'ApprovedNameUsage',
          label: 'Clientname usage approved?',
          className: 'text-left',
        },
        { name: 'Comment', label: 'Comment', className: 'text-left', width: TabListTableCellWidthEnum.TwoLines },

        { name: '-', label: '' },
        { name: '-message-', label: 'SLA & Storage' },
        {
          name: 'SecurityIncidentNotificationDisplay',
          label: 'Security Incident Notification',
          className: 'text-left',
        },
        { name: 'PrivacyBreachNotificationDisplay', label: 'Privacy Breach Notification', className: 'text-left' },
        { name: 'SubjectInquiryNotificationDisplay', label: 'Subject Inquiry Notification', className: 'text-left' },
        { name: 'ErasureNotificationDisplay', label: 'Erasure Notification', className: 'text-left' },

        {
          name: 'DataFlowMapDisplay',
          label: 'Data Storage Location',
          className: 'text-left',
          width: TabListTableCellWidthEnum.TwoLines,
        },
        {
          name: 'DataFlowMapFreeText',
          label: 'Data Storage Location Description',
          className: 'text-left',
          width: TabListTableCellWidthEnum.TwoLines,
        },

        { name: '-', label: '' },
        {
          name: '-message-',
          label: 'Data Management',
        },
        {
          name: 'ClientAccountContactsDataDisplay',
          label: 'Client Account Contacts Personal Data',
          className: 'text-left',
        },
        {
          name: 'ClientOperationalContactsDataDisplay',
          label: 'Client Operational Contacts Personal Data',
          className: 'text-left',
        },
        { name: 'DeliveredEvaluationDetailsDisplay', label: 'Delivered Evaluation Details', className: 'text-left' },

        {
          name: (item) => JsxHelper.YesNo(item.ClientOperationalContactsExport),
          label: 'Export Client operational contacts at termination?',
          className: 'text-left',
        },
        {
          name: (item) => JsxHelper.YesNo(item.DeliveredEvaluationDetailsExport),
          label: 'Export evaluation details at termination?',
          className: 'text-left',
        },
        {
          name: 'PublicInformationClassification',
          label: 'Public Information Classification',
          className: 'text-left',
          width: TabListTableCellWidthEnum.TwoLines,
        },
        {
          name: 'ConfidentialInformationClassification',
          label: 'Confidential Information Classification',
          className: 'text-left',
          width: TabListTableCellWidthEnum.TwoLines,
        },
        {
          name: 'InternalInformationClassification',
          label: 'Internal Information Classification',
          className: 'text-left',
          width: TabListTableCellWidthEnum.TwoLines,
        },
        {
          name: 'RestrictedInformationClassification',
          label: 'Restricted Information Classification',
          className: 'text-left',
          width: TabListTableCellWidthEnum.TwoLines,
        },

        { name: '-', label: '' },
        { name: '-message-', label: 'Data Transfer & Retention' },
        {
          name: (item) => JsxHelper.YesNo(item.DataTransferRequired),
          label: 'Data transfer required? (Section A)',
          className: 'text-left',
        },
        {
          name: (item) => JsxHelper.YesNo(item.DataRetentionDefined),
          label: 'Data retention defined? (Section B)',
          className: 'text-left',
        },
        { name: 'DataRetainUntilDisplay', label: 'Retain data until', className: 'text-left' },
      ],
    },
  ];

  static FieldsToShowInDataTable() {
    return [
      {
        label: 'OpportunityId',
        orderById: 'OpportunityId',
        data: 'OpportunityId',
        type: FieldTypesEnum.DataFieldWithDetails,
        minWidth: 70,
      },
    ];
  }
}

export default OpportunitySecurityChecklistItem;
